import { Box, Button, Container } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // FIX 1: Initialize state based on localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("404-theme-mode");
    if (savedTheme) {
      return savedTheme === "dark";
    }
    // Check system preference
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  console.log("Current Path:", location.pathname);
  console.log(
    "Previous State:",
    location.state?.from || "No previous state provided"
  );

  // FIX 2: Persist theme preference
  useEffect(() => {
    localStorage.setItem("404-theme-mode", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // FIX 3: Wrap createTheme in useMemo to prevent unnecessary re-renders
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? "dark" : "light",
          background: {
            default: isDarkMode ? "#050505" : "#fff",
          },
          text: {
            primary: isDarkMode ? "#fff" : "#000",
          },
          primary: {
            main: "#faca2e",
          },
        },
        typography: {
          fontFamily: '"Fira Sans", sans-serif',
        },
      }),
    [isDarkMode]
  );

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // FIX 4: Helper function to determine where to go
  const handleGoBack = () => {
    // If we have a 'from' state, go there. Otherwise, go to dashboard or home
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      // Try to go to dashboard, fallback to home
      navigate("/", { replace: true });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        component="main"
        className="error-page"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: isDarkMode ? "#050505" : "#fff",
          color: isDarkMode ? "#fff" : "#000",
          transition: "all 0.3s ease-in-out",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Theme Toggle Button */}
        <Box
          component="button"
          onClick={toggleTheme}
          sx={{
            position: "fixed",
            top: 40,
            right: 40,
            backgroundColor: "transparent",
            fontSize: "30px",
            cursor: "pointer",
            color: "#faca2e",
            border: "none",
            padding: 0,
            zIndex: 1000,
            "&:hover": {
              transform: "scale(1.2)",
            },
            transition: "transform 0.3s ease-in-out",
          }}
          aria-label="toggle theme"
          title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
        >
          {isDarkMode ? "☀️" : "🌙"}
        </Box>

        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              rowGap: "30px",
              textAlign: "center",
              margin: "auto",
              animation: "fadeIn 0.6s ease-in-out",
              "@keyframes fadeIn": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            {/* Animated Eyes */}
            <Box
              className="eyes"
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              {[0, 1].map((eyeIndex) => (
                <Box
                  key={eyeIndex}
                  sx={{
                    width: { xs: "60px", sm: "80px" },
                    height: { xs: "60px", sm: "80px" },
                    backgroundColor: "#faca2e",
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    position: "relative",
                    boxShadow: "0 4px 15px rgba(250, 202, 46, 0.3)",
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "22px", sm: "30px" },
                      height: { xs: "22px", sm: "30px" },
                      backgroundColor: "#050505",
                      borderRadius: "50%",
                      animation:
                        eyeIndex === 1
                          ? "movePupilRight 2s infinite ease-in-out"
                          : "movePupilLeft 2s infinite ease-in-out",
                      "@keyframes movePupilLeft": {
                        "0%, 100%": { transform: "translate(0, 0)" },
                        "25%": { transform: "translate(-10px, -10px)" },
                        "50%": { transform: "translate(10px, 10px)" },
                        "75%": { transform: "translate(-10px, 10px)" },
                      },
                      "@keyframes movePupilRight": {
                        "0%, 100%": { transform: "translate(0, 0)" },
                        "25%": { transform: "translate(10px, 10px)" },
                        "50%": { transform: "translate(-10px, -10px)" },
                        "75%": { transform: "translate(10px, -10px)" },
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Heading Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Box
                component="h1"
                sx={{
                  fontSize: { xs: "30px", sm: "36px" },
                  fontWeight: 500,
                  color: "#faca2e",
                  margin: 0,
                  textTransform: "capitalize",
                }}
              >
                Looks like you're lost
              </Box>
              <Box
                component="p"
                sx={{
                  fontSize: { xs: "22px", sm: "26px" },
                  fontWeight: 200,
                  margin: 0,
                  color: isDarkMode ? "#ccc" : "#666",
                }}
              >
                404 error
              </Box>
            </Box>

            {/* Back to Home Button */}
            <Button
              onClick={handleGoBack}
              sx={{
                color: "inherit",
                textDecoration: "none",
                border: "1px solid #faca2e",
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 200,
                padding: { xs: "12px 24px", sm: "15px 30px" },
                borderRadius: "15px",
                boxShadow: "0px 7px 0px -2px #faca2e",
                transition: "all 0.3s ease-in-out",
                textTransform: "capitalize",
                backgroundColor: "transparent",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "#faca2e",
                  color: "#fff",
                  transform: "translateY(3px)",
                },
                "&:active": {
                  transform: "translateY(5px)",
                },
              }}
            >
              back to home
            </Button>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default NotFound;
